import type { AnswerState } from '@repo/contracts/cardfinder/funnel/types';
import type { BranchId } from '../../config/constants';
import type {
  FlattenedBranch,
  FlattenedBranchStep,
  FlattenedFunnelConfig,
} from '../../config/flattened/types';
import { ENTRY_STEP_ID, RECOMMENDATIONS } from './constants';

export function getBranchForPrimaryGoal(
  config: FlattenedFunnelConfig,
  answer: string,
): BranchId | undefined {
  return config.branchByPrimaryGoal[answer];
}

/** After primary_goal, the next step is the entry of the selected branch. */
export function getNextStepFromPrimaryGoal(
  config: FlattenedFunnelConfig,
  answer: string,
): string | undefined {
  const branchId = getBranchForPrimaryGoal(config, answer);
  if (!branchId) return undefined;
  return config.branches[branchId]?.entry;
}

export function getBranchStep(
  config: FlattenedFunnelConfig,
  branchId: BranchId,
  stepId: string,
): FlattenedBranch['steps'][number] | undefined {
  return config.branches[branchId]?.steps.find(s => s.step.id === stepId);
}

/**
 * Resolve the next step within a branch given the current step and (optionally)
 * the answer just submitted. Returns:
 *   - StepId          → navigate to that step
 *   - 'recommendations' → the step's transition is 'end'; caller should redirect
 *                       to the recommendations page
 *   - undefined       → unknown step, missing answer for a by-answer transition,
 *                       or unmapped answer value (caller decides how to handle)
 */
export function resolveNextStepId(
  config: FlattenedFunnelConfig,
  branchId: BranchId,
  currentStepId: string,
  answer?: string,
): string | undefined {
  const branchStep = getBranchStep(config, branchId, currentStepId);
  if (!branchStep) return undefined;
  const t = branchStep.transition;
  if (t.kind === 'end') return RECOMMENDATIONS;
  if (t.kind === 'static') return t.next;
  if (answer === undefined) return undefined;
  return t.next[answer];
}

/**
 * Whether a step counts as answered for the purpose of walking past it. A
 * skipped step stores no answer at all, so its absence is not evidence the user
 * never got there.
 *
 * Composed with `resolveNextStepId` by every walker in this module: this decides
 * whether the path may continue, that decides where it goes.
 */
export function isStepSatisfied(branchStep: FlattenedBranchStep, answers: AnswerState): boolean {
  return branchStep.step.skippable || answers[branchStep.step.id] !== undefined;
}

/**
 * Returns true if `targetStepId` is reachable from the branch entry step given
 * the current `answers`. Requires `primary_goal` to map to `branchId` (so a branch
 * entry is not vacuously reachable with another branch's session), and that every
 * step along the path has a stored answer unless the step is skippable.
 */
export function canReachStep(
  config: FlattenedFunnelConfig,
  branchId: BranchId,
  targetStepId: string,
  answers: AnswerState,
): boolean {
  const primaryGoal = answers[ENTRY_STEP_ID];
  if (typeof primaryGoal !== 'string') return false;
  // Travel/building_credit both use credit_score as entry — without this, the
  // entry step looks reachable for any defined primary_goal.
  if (getBranchForPrimaryGoal(config, primaryGoal) !== branchId) return false;

  const branch = config.branches[branchId];
  if (!branch) return false;

  let currentId = branch.entry;
  const visited = new Set<string>();

  while (currentId !== targetStepId) {
    if (visited.has(currentId)) return false;
    visited.add(currentId);

    const branchStep = getBranchStep(config, branchId, currentId);
    if (!branchStep) return false;
    if (!isStepSatisfied(branchStep, answers)) return false;

    const answer = answers[currentId] as string | undefined;
    const next = resolveNextStepId(config, branchId, currentId, answer);
    // Running out of branch, or ending it, means the target was never on the path.
    if (!next || next === RECOMMENDATIONS) return false;
    currentId = next;
  }

  return true;
}

/**
 * Resolve the previous step on the path the user took through a branch, based on
 * stored answers. Returns:
 *   - StepId    → the step before `currentStepId` on that path
 *   - undefined → branch entry (caller should navigate to primary goal), unknown
 *                 step, unreachable current step, or missing answer on the path
 */
export function getPreviousStepId(
  config: FlattenedFunnelConfig,
  branchId: BranchId,
  currentStepId: string,
  answers: AnswerState,
): string | undefined {
  const branch = config.branches[branchId];
  if (!branch) return undefined;
  if (currentStepId === branch.entry) return undefined;

  let previousStepId: string | undefined;
  let stepId = branch.entry;
  const visited = new Set<string>();

  while (stepId !== currentStepId) {
    if (visited.has(stepId)) return undefined;
    visited.add(stepId);

    const branchStep = branch.steps.find(s => s.step.id === stepId);
    if (!branchStep) return undefined;

    const answer = answers[stepId];
    const nextStepId = resolveNextStepId(
      config,
      branchId,
      stepId,
      typeof answer === 'string' ? answer : undefined,
    );
    if (!nextStepId || nextStepId === RECOMMENDATIONS) return undefined;

    previousStepId = stepId;
    stepId = nextStepId;
  }

  return previousStepId;
}
